'use client';

// ia-cc-2 (epic IA custo-consciente): bloco padrão de "análise IA" (Modo A).
// Encapsula os 4 estados — vazio (botão), gerando, pronto (IA salva + regerar),
// erro — com o princípio custo-consciente: a GERAÇÃO é sempre uma ação manual
// (onGerar); abrir/exibir não chama o modelo. Reusável por qualquer tela que
// tenha uma análise IA opcional sobre um conteúdo base determinístico.

import type { ReactNode } from 'react';
import Button from './Button';
import Chip from './Chip';

export interface AnaliseIaEstado {
  texto: string;
  origem: 'ia' | 'regra' | 'nenhuma';
  geradoEm?: string | null;
}

export interface BlocoAnaliseIaProps {
  titulo: string;
  /** Estado atual da análise (null enquanto carrega do backend). */
  analise: AnaliseIaEstado | null;
  /** Texto determinístico exibido quando ainda não há análise de IA salva. */
  textoBase?: string;
  gerando: boolean;
  onGerar: () => void;
  rotuloGerar?: string;
  erro?: string | null;
  /** Conteúdo específico da tela (ex.: resumo do recomendado), antes do texto. */
  children?: ReactNode;
}

export default function BlocoAnaliseIa({
  titulo,
  analise,
  textoBase,
  gerando,
  onGerar,
  rotuloGerar = 'Analisar com IA',
  erro,
  children,
}: BlocoAnaliseIaProps) {
  const iaSalva = analise?.origem === 'ia';
  const texto = iaSalva ? analise!.texto : (textoBase ?? '');
  const geradoEm = iaSalva && analise?.geradoEm
    ? new Date(analise.geradoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    : null;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-[var(--ai-100)] bg-[var(--ai-50)] p-3">
      <i className="pi pi-sparkles mt-0.5 text-[var(--ai-500)]" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-bold uppercase tracking-[0.05em] text-[var(--ai-600)]">{titulo}</span>
          {iaSalva && (
            <Chip variant="ai">
              <i className="pi pi-sparkles mr-1 text-[9px]" aria-hidden="true" />
              Gerada por IA
            </Chip>
          )}
          {geradoEm && <span className="text-[10.5px] text-[var(--fg-subtle)]">em {geradoEm}</span>}
        </div>

        {children}

        {texto && (
          <p className="mt-1.5 text-[12px] leading-relaxed text-[var(--fg)]">{texto}</p>
        )}

        {erro && (
          <p className="mt-1.5 text-[11.5px] text-[var(--warning-700)]">
            <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{erro}
          </p>
        )}

        <div className="mt-2">
          <Button
            variant={iaSalva ? 'ghost' : 'default'}
            size="sm"
            icon={gerando ? 'pi pi-spin pi-spinner' : 'pi pi-sparkles'}
            disabled={gerando}
            onClick={onGerar}
          >
            {gerando ? 'Gerando análise…' : iaSalva ? 'Regerar análise' : rotuloGerar}
          </Button>
        </div>
      </div>
    </div>
  );
}
